/**
 * AddComputation is the main component of the page used to add or edit a computation.
 * It is encapsulated in a falcon card.
 * This component contains the context,
 * and the subcomponents used in this page: Datagrid and AddEditPopup.
 * This component fetches the data from the API and passes it to the subcomponents.
 * It also POST/PATCH the data to the API when the user confirms the form.
 *
 * editingComputation is the intelligence of the component. It can be:
 * - null: no computation is being edited
 * - {}: an empty computation object, meaning the user is adding a new computation
 * - {id: <computation_id>}: a computation object, meaning the user is editing an existing computation
 * Depending on the value of editingComputation, the component will either open the add popup or the edit popup
 * and call the API to POST/PATCH the data.
 */

import React, { useState, useEffect } from "react";
import Datagrid from "./Datagrid";
import AddEditPopup from "./AddEditPopup";
import { osirisApi } from "../../../../../services/api/osiris";
import { useAuth } from "../../../../../providers/AuthProvider";
import PyramidCard from "components/common/pyramid/PyramidCard";
import notify from "devextreme/ui/notify";

/**
 * Main component
 */
const AddComputation = () => {
  const osiris = osirisApi();
  const { accessToken, companyList, userList } = useAuth();

  /* State variables */
  const [isLoading, setIsLoading] = useState(true);
  const [computations, setComputations] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [editingComputation, setEditingComputation] = useState(null);

  /**
   * Fetches the data from the API.
   */
  const fetchData = async () => {
    if (!accessToken) {
      return;
    }

    setIsLoading(true);
    try {
      const [datasetsData, computationsData] = await Promise.all([
        osiris.getDatasets({
          onlyReady: true,
          includeUserTransitiveGroups: true,
        }),
        osiris.getComputations({
          onlyReady: false,
          includeUserTransitiveGroups: true,
        }),
      ]);
      const computations = Array.isArray(computationsData)
        ? computationsData
        : computationsData.result || [];
      const datasets = Array.isArray(datasetsData)
        ? datasetsData
        : datasetsData.result || [];
      setComputations(computations);
      setDatasets(datasets);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Fetches the data from the API when the component mounts or when the accessToken changes.
   */
  useEffect(() => {
    fetchData();
  }, [accessToken]);

  /**
   * Fetches the data from the API when the refreshComputations event is triggered.
   */
  useEffect(() => {
    const handleRefresh = () => {
      fetchData();
    };

    window.addEventListener("refreshComputations", handleRefresh);
    return () =>
      window.removeEventListener("refreshComputations", handleRefresh);
  }, [accessToken]);

  // Using the editingComputation state to determine if the add/edit popup is visible and if we are in edit mode
  const isAddEditPopupVisible = Boolean(editingComputation !== null);
  const editMode = Boolean(editingComputation?.id);

  /**
   * Handles the row click event in the datagrid.
   * Sets the editingComputation state to the row data which will open the edit popup on edit mode.
   *
   * @param {object} data - The data of the clicked row
   */
  const handleRowClick = (rowData) => {
    if (rowData.bucketRules !== 0) {
      rowData.Bucket = true;
    }
    if (rowData.actuarialRateRules !== 0) {
      rowData.ActuarialRate = true;
    }
    setEditingComputation(rowData);
  };

  /**
   * Opens the add popup on add mode by setting the editingComputation state to an empty object.
   */
  const handleAddButtonClick = () => {
    setEditingComputation({});
  };

  /**
   * Handles the cancel button click, and closes the add/edit popup by setting the editingComputation state to null.
   */
  const onCancel = () => {
    setEditingComputation(null);
  };

  /**
   * Confirms the add/edit action and updates the dataSource.
   * Handles both add and edit cases.
   *
   * @param {object} data - The updated data to be saved
   */
  const onConfirm = async (data) => {
    try {
      if (editMode) {
        const updatedData = {
          id: editingComputation.id,
          datasetId: editingComputation.datasetId,
          description: data.description,
        };
        await osiris.updateComputation(updatedData);
        fetchData();
        setEditingComputation(null);
      } else {
        await osiris.addComputation(data);
        fetchData();
        setEditingComputation(null);
      }
    } catch (error) {
      console.error("Error:", error.message);
      notify(error.message || "An error occurred", "error", 3000);
    }
  };

  return (
    <div>
      <Datagrid
        companyList={companyList}
        userList={userList}
        computationList={computations}
        datasetList={datasets}
        onAddButtonClick={handleAddButtonClick}
        onRowClick={handleRowClick}
        isLoading={isLoading}
      />
      <AddEditPopup
        isVisible={isAddEditPopupVisible}
        onCancel={onCancel}
        onConfirm={onConfirm}
        inputFormData={editingComputation || {}}
        datasetList={datasets}
        editMode={editMode}
      />
    </div>
  );
};

/**
 * Page component using PyramidCard
 */
const AddComputationPage = () => {
  return (
    <PyramidCard title="Computation">
      <AddComputation />
    </PyramidCard>
  );
};

export default AddComputationPage;
